import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Users, UserPlus, UsersRound, FileText, Receipt, DollarSign, HelpCircle, ShieldOff } from 'lucide-react';
import HospitalsTab from './admin/HospitalsTab';
import UsersTab from './admin/UsersTab';
import FamiliesTab from './admin/FamiliesTab';
import MembersTab from './admin/MembersTab';
import PricelistsTab from './admin/PricelistsTab';
import ClaimsTab from './admin/ClaimsTab';
import CurrenciesTab from './admin/CurrenciesTab';
import SecurityTab from './admin/SecurityTab';
import { API } from './admin/adminShared';

const AdminCRUD = () => {
  const navigate = useNavigate();

  const [hospitals, setHospitals] = useState([]);
  const [users, setUsers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [members, setMembers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const username = localStorage.getItem('username');

  const isSuperAdmin = username === 'superadmin';
  const isDataEntry = role === 'DataEntry';

  const authCfg = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadData = useCallback(async () => {
    try {
      const [h, u, f, m, c] = await Promise.all([
        axios.get(`${API}/admin/hospitals`, authCfg),
        axios.get(`${API}/admin/users`, authCfg),
        axios.get(`${API}/admin/families`, authCfg),
        axios.get(`${API}/admin/members`, authCfg),
        axios.get(`${API}/currencies`, authCfg),
      ]);
      setHospitals(h.data);
      setUsers(u.data);
      setFamilies(f.data);
      setMembers(m.data);
      setCurrencies(c.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (isDataEntry) {
      toast.error('Access denied. DataEntry users can only use bulk upload features.');
      navigate('/admin');
      return;
    }
    if (!isSuperAdmin) {
      toast.error('Access denied. Superadmin only.');
      navigate('/dashboard');
      return;
    }
    loadData().finally(() => setInitialLoading(false));
    // eslint-disable-next-line
  }, []);

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f4f8 100%)' }}>
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3" data-testid="admin-back-btn">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </Button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Admin Panel</h1>
                <p className="text-xs sm:text-sm text-gray-600">System Management</p>
              </div>
            </div>
            <Button onClick={() => navigate('/help')} variant="outline" size="sm" className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50 self-end sm:self-auto" data-testid="admin-help-btn">
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Help</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        <Tabs defaultValue="hospitals" className="space-y-4 sm:space-y-6">
          <TabsList className="h-auto w-full flex flex-wrap gap-1 bg-white border border-gray-200 p-1 rounded-lg overflow-visible" data-testid="admin-tabs">
            <TabsTrigger value="hospitals" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-hospitals"><Building2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Hospitals</span></TabsTrigger>
            <TabsTrigger value="users" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-users"><UserPlus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Users</span></TabsTrigger>
            <TabsTrigger value="families" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-families"><UsersRound className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Families</span></TabsTrigger>
            <TabsTrigger value="members" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-members"><Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Members</span></TabsTrigger>
            <TabsTrigger value="pricelists" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-pricelists"><FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Price Lists</span></TabsTrigger>
            <TabsTrigger value="bills" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-claims"><Receipt className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Claims</span></TabsTrigger>
            <TabsTrigger value="currencies" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-currencies"><DollarSign className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Currencies</span></TabsTrigger>
            {username === 'superadmin' && (
              <TabsTrigger value="security" className="flex-auto text-xs sm:text-sm px-2 sm:px-3" data-testid="admin-tab-security"><ShieldOff className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" /><span className="hidden sm:inline">Security</span></TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="hospitals">
            <HospitalsTab hospitals={hospitals} currencies={currencies} reload={loadData} authCfg={authCfg} />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab users={users} hospitals={hospitals} reload={loadData} authCfg={authCfg} />
          </TabsContent>
          <TabsContent value="families">
            <FamiliesTab families={families} members={members} reload={loadData} authCfg={authCfg} />
          </TabsContent>
          <TabsContent value="members">
            <MembersTab families={families} reload={loadData} authCfg={authCfg} />
          </TabsContent>
          <TabsContent value="pricelists">
            <PricelistsTab hospitals={hospitals} currencies={currencies} authCfg={authCfg} />
          </TabsContent>
          <TabsContent value="bills">
            <ClaimsTab hospitals={hospitals} members={members} authCfg={authCfg} />
          </TabsContent>
          <TabsContent value="currencies">
            <CurrenciesTab currencies={currencies} reload={loadData} authCfg={authCfg} />
          </TabsContent>
          {username === 'superadmin' && (
            <TabsContent value="security">
              <SecurityTab authCfg={authCfg} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminCRUD;
